import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getNewsByFilter } from "../api/apiHandler";
import { getMediaBaseUrl } from "../utils/backendUrl";

const getEmbedUrl = (url) => {
  if (!url) return "";
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  return url.includes("youtube.com/") || url.includes("youtu.be") ? "" : url;
};

const VideoHub = () => {
  const [videos, setVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const BASE_URL = getMediaBaseUrl();

  const mapVideo = (item) => {
    let imageUrl = "";
    if (item.thumbnail) {
      if (item.thumbnail.startsWith("http")) {
        imageUrl = item.thumbnail;
      } else if (item.thumbnail.startsWith("/images/")) {
        imageUrl = item.thumbnail;
      } else {
        imageUrl = `${BASE_URL}/uploads/news/${encodeURIComponent(item.thumbnail)}`;
      }
    }
    return {
      id: item.id,
      title: item.news_title || item.title || "",
      category: item.category || "",
      link: `/news/${item.id}`,
      url: getEmbedUrl(item.youtube_url || ""),
      image: imageUrl,
      date: item.createdAt ? new Date(item.createdAt).toLocaleDateString("hi-IN") : "",
      description: item.description || "",
    };
  };

  useEffect(() => {
    let isMounted = true;
    const loadVideos = async () => {
      try {
        const res = await getNewsByFilter({ category: "video news", limit: 15 });
        let items = Array.isArray(res?.data) ? res.data : [];

        if (items.length === 0) {
          const resVideo = await getNewsByFilter({ has_video: "1", limit: 15 });
          items = Array.isArray(resVideo?.data) ? resVideo.data : [];
        }

        if (isMounted) {
          const mapped = items
            .map(mapVideo)
            .filter((v) => v.url !== "");
          setVideos(mapped);
          setActiveIndex(0);
        }
      } catch (err) {
        console.error("Error fetching video news:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadVideos();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="my-6 md:my-10 py-8 md:py-12 bg-rich-black -mx-4 px-4 lg:-mx-8 lg:px-8 rounded-none">
        <p className="text-white text-center font-semibold">वीडियो लोड हो रहे हैं...</p>
      </div>
    );
  }

  const activeVideo = videos[activeIndex];

  if (!activeVideo) {
    return (
      <div className="my-6 md:my-10 py-8 md:py-12 bg-rich-black -mx-4 px-4 lg:-mx-8 lg:px-8 rounded-none">
        <div className="flex justify-between items-end mb-6">
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-wide flex items-center gap-3">
            <i className="fa-solid fa-circle-play text-brand-red"></i> वीडियो हब
          </h2>
          <Link to="/gallery/videos" className="text-gray-400 hover:text-white text-sm font-bold transition">
            सभी देखें &rarr;
          </Link>
        </div>
        <p className="text-gray-400 text-center">अभी कोई वीडियो उपलब्ध नहीं है।</p>
      </div>
    );
  }

  return (
    <div className="my-6 md:my-10 py-8 md:py-12 bg-rich-black -mx-4 px-4 lg:-mx-8 lg:px-8 rounded-none">
      <div className="flex justify-between items-end mb-6">
        <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-wide flex items-center gap-3">
          <i className="fa-solid fa-circle-play text-brand-red"></i> वीडियो हब
        </h2>
        <Link to="/gallery/videos" className="text-gray-400 hover:text-white text-sm font-bold transition">
          सभी देखें &rarr;
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Featured Video (Left, 8 cols) */}
        <div className="lg:col-span-8">
          <div className="bg-black rounded-lg overflow-hidden border border-zinc-800 relative">
            <div className="w-full aspect-video relative">
              <iframe
                key={activeVideo.id}
                className="absolute inset-0 w-full h-full"
                src={activeVideo.url}
                title={activeVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="p-4 bg-zinc-900 border-t border-zinc-800">
              <h3 className="text-white text-lg md:text-xl font-bold">
                <Link to={activeVideo.link} className="hover:text-brand-red transition">
                  {activeVideo.title}
                </Link>
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                {activeVideo.category} • {activeVideo.date}
              </p>
            </div>
          </div>
        </div>

        {/* Playlist (Right, 4 cols) */}
        <div className="lg:col-span-4 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden flex flex-col">
          <div className="bg-zinc-800 text-white font-bold px-4 py-3 border-b border-zinc-700 text-sm uppercase tracking-wide">
            वीडियो सूची
          </div>
          <div className="flex-1 overflow-y-auto max-h-[350px] lg:max-h-[420px] p-2 space-y-2 scrollbar-thin scrollbar-thumb-zinc-700">
            {videos.map((item, i) => (
              <button
                key={item.id}
                onClick={() => setActiveIndex(i)}
                className={`w-full text-left flex gap-3 p-2 rounded-md cursor-pointer transition group ${
                  i === activeIndex ? "bg-brand-red/20 ring-1 ring-brand-red" : "hover:bg-zinc-800"
                }`}
              >
                <div className="relative w-28 shrink-0 aspect-video rounded overflow-hidden">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <i className="fa-solid fa-video text-zinc-600"></i>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <i className="fa-solid fa-play text-white opacity-80 group-hover:opacity-100 group-hover:text-brand-red transition"></i>
                  </div>
                </div>
                <div className="flex flex-col justify-center">
                  <h4 className="text-gray-200 text-[13px] font-bold line-clamp-3 group-hover:text-brand-red transition leading-snug">
                    {item.title}
                  </h4>
                  <span className="text-zinc-500 text-[10px] mt-1 uppercase font-bold">
                    {item.category}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoHub;
